// PaySimple Dashboard — thin config over the shared ReconDashboard shell. All layout/behavior lives in
// ReconDashboard; this file supplies only PaySimple's data (endpoints, single-account, tiles).
import React from 'react';
import ReconDashboard from '../../components/recon/ReconDashboard';
import { buildPaysimpleNav } from '../../config/recon/paysimpleNav';
import { fmtMoney, fmtLastSynced } from '../../components/recon/reconShared';
import { get } from '../../utils/api';

const isActive = (s) => /active/i.test(String(s || ''));

export const paysimpleDashConfig = {
  provider: 'paysimple',
  title: 'Dashboard',
  accounts: false,
  syncTitle: 'Sync now — reload PaySimple data',
  buildNav: (active, account, navigate) => buildPaysimpleNav(active, navigate),

  load: async () => {
    const [subs, recon] = await Promise.all([
      get('/api/manage/paysimple-schedules'),
      get('/api/manage/paysimple-reconcile', { year: 'all', month: 'all' }),
    ]);
    let maps = {};
    try { const mm = await get('/api/manage/paysimple-schedules/maps'); (mm?.mappings || []).forEach((x) => { maps[String(x.schedule_id)] = x; }); } catch { /* non-fatal */ }
    return { subs, recon, maps };
  },
  lastSynced: (d) => fmtLastSynced(d?.recon?.last_run),

  sections: ({ data, navigate }) => {
    const recon = data?.recon || {}; const counts = recon.counts || {}; const amounts = recon.amounts || {};
    const maps = data?.maps || {};
    const schedules = Array.isArray(data?.subs?.schedules) ? data.subs.schedules : [];
    const active = schedules.filter((s) => isActive(s.status));
    const mappedCount = schedules.filter((s) => maps[String(s.id)]).length;
    const monthly = active.reduce((a, s) => a + (Number(s.amount) || 0), 0);
    const returnedTotal = (counts.returned ?? 0) + (counts.refunded ?? 0) + (counts.voided ?? 0) + (counts.failed ?? 0);
    const subsPath = '/payments/paysimple/subscriptions';
    const reconPath = '/payments/paysimple/reconciliation';

    return [
      {
        label: 'Recurring (subscriptions)',
        tiles: [
          { label: 'Active subscriptions', value: active.length, sub: 'recurring now', color: 'primary', onClick: () => navigate(subsPath) },
          { label: 'Mapped', value: mappedCount, sub: 'devotee + allocation', color: 'success', onClick: () => navigate(subsPath) },
          { label: 'Needs mapping', value: Math.max(0, schedules.length - mappedCount), sub: 'set up allocation', color: (schedules.length - mappedCount) > 0 ? 'warning' : 'success', onClick: () => navigate(subsPath) },
          { label: 'Monthly recurring', value: fmtMoney(monthly), sub: 'sum of active', color: null },
        ],
      },
      {
        label: 'Transaction reconciliation',
        tiles: [
          { label: 'Payments', value: recon.total ?? '—', sub: 'all stored', color: null, onClick: () => navigate(reconPath) },
          { label: 'Has order', value: (counts.matched ?? 0) + (counts.needs_link ?? 0), sub: 'matched / needs-link', color: 'success', onClick: () => navigate(reconPath) },
          { label: 'No order · at risk', value: counts.unbooked ?? 0, sub: fmtMoney(amounts.unbooked), color: 'error', onClick: () => navigate(reconPath) },
          { label: 'Returned / failed', value: returnedTotal, sub: fmtMoney((amounts.returned ?? 0) + (amounts.refunded ?? 0) + (amounts.failed ?? 0)), color: 'warning', onClick: () => navigate(reconPath) },
        ],
      },
    ];
  },
};

export default function PaysimpleDashboardNewPage() {
  return <ReconDashboard config={paysimpleDashConfig} />;
}
